import { Box, Flex, Text } from "@radix-ui/themes";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";

interface SectorData {
  sector: string;
  portfolio_return: number;
  sector_return: number;
}

interface ChartConfig {
  yaxis_range?: [number, number];
  yaxis_title?: string;
  bar_colors?: {
    portfolio: string;
    sector: string;
  };
}

interface SectorChartProps {
  data: {
    data: SectorData[];
    chart_config?: ChartConfig;
  };
  portfolioLabel?: string; // Optional label to replace "Portfolio Return" (e.g., ticker symbol)
}

interface ChartDataPoint {
  sector: string;
  portfolioReturn: number;
  sectorReturn: number;
  displayName: string;
}

interface TooltipData {
  sector: string;
  portfolioReturn: number;
  sectorReturn: number;
}

interface TooltipPayload {
  payload: TooltipData;
}

const CustomTooltip = ({
  active,
  payload,
  portfolioLabel,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
  portfolioLabel?: string;
}) => {
  if (!active || !payload?.length) return null;

  const data = payload[0].payload;
  const label = portfolioLabel || "Portfolio";
  return (
    <Box
      p="2"
      style={{
        backgroundColor: "var(--color-background)",
        border: "1px solid var(--gray-6)",
        borderRadius: "var(--radius-2)",
        boxShadow: "var(--shadow-3)",
      }}
    >
      <Text size="1" weight="medium" highContrast>
        {data.sector}
      </Text>
      <br />
      <Text size="1" color="blue">
        {label}: {(data.portfolioReturn * 100).toFixed(2)}%
      </Text>
      <br />
      <Text size="1" highContrast>
        Sector: {(data.sectorReturn * 100).toFixed(2)}%
      </Text>
    </Box>
  );
};

const formatYAxisTick = (value: number): string => {
  if (value === 0) return "0";
  return (value * 100).toFixed(3);
};

const formatXAxisTick = (
  value: string,
  index: number,
  data: ChartDataPoint[]
): string => {
  const item = data[index];
  return item?.displayName || value;
};

const SectorChart = ({ data, portfolioLabel }: SectorChartProps) => {
  if (!data?.data?.length) return null;

  const chartData: ChartDataPoint[] = data.data.map((item) => ({
    sector: item.sector,
    portfolioReturn: item.portfolio_return,
    sectorReturn: item.sector_return,
    displayName:
      item.sector.length > 15
        ? `${item.sector.substring(0, 12)}...`
        : item.sector,
  }));

  const allValues = chartData.flatMap((d) => [
    d.portfolioReturn,
    d.sectorReturn,
  ]);
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const padding = (maxValue - minValue) * 0.1;
  const domain = data.chart_config?.yaxis_range || [
    minValue - padding,
    maxValue + padding,
  ];

  const leftMargin = 10;
  const label = portfolioLabel || "Portfolio";

  return (
    <Box style={{ width: "100%", height: "271px" }}>
      <Flex direction="column" gap="2" style={{ height: "100%" }}>
        <Flex align="center" gap="3" mb="4">
          <Flex align="center" gap="1">
            <Box
              style={{
                width: "12px",
                height: "12px",
                backgroundColor: "var(--blue-9)",
                borderRadius: "var(--radius-1)",
              }}
            />
            <Text size="1" color="gray">
              {label} Return
            </Text>
          </Flex>
          <Flex align="center" gap="1">
            <Box
              style={{
                width: "12px",
                height: "12px",
                backgroundColor: "var(--gray-12)",
                borderRadius: "var(--radius-1)",
              }}
            />
            <Text size="1" color="gray">
              Sector Return
            </Text>
          </Flex>
        </Flex>

        <Box style={{ flex: 1, position: "relative" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 0, right: 0, left: leftMargin, bottom: 20 }}
              barCategoryGap="20%"
              maxBarSize={35}
            >
              <XAxis
                dataKey="sector"
                axisLine={false}
                tickLine={false}
                tick={{
                  fontSize: 12,
                  fill: "var(--gray-11)",
                  textAnchor: "middle",
                  fontFamily: "inherit",
                }}
                tickFormatter={(value, index) =>
                  formatXAxisTick(value, index, chartData)
                }
                interval={0}
                height={20}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{
                  fontSize: 12,
                  fill: "var(--gray-11)",
                  textAnchor: "end",
                  fontFamily: "inherit",
                }}
                tickFormatter={formatYAxisTick}
                domain={domain}
                width={40}
              />
              <Tooltip
                content={<CustomTooltip portfolioLabel={portfolioLabel} />}
              />

              <ReferenceLine y={0} stroke="var(--gray-8)" strokeWidth={0.5} />
              <Bar
                dataKey="portfolioReturn"
                fill="var(--blue-9)"
                radius={[2, 2, 0, 0]}
                maxBarSize={35}
              />
              <Bar
                dataKey="sectorReturn"
                fill="var(--gray-12)"
                radius={[2, 2, 0, 0]}
                maxBarSize={35}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Flex>
    </Box>
  );
};

export default SectorChart;
